export type AdminRole = 'SUPPORT' | 'ADMIN' | 'OWNER';

export const ALL_ADMIN_ROLES: AdminRole[] = ['SUPPORT', 'ADMIN', 'OWNER'];

// Доступ к страницам (ключ = id пункта меню в AdminSidebar)
export const ADMIN_PAGE_ACCESS: Record<string, AdminRole[]> = {
  support:            ALL_ADMIN_ROLES,
  shop:               ['ADMIN', 'OWNER'],
  roulette:           ['ADMIN', 'OWNER'],
  referrals:          ['ADMIN', 'OWNER'],
  'promo-codes':      ['ADMIN', 'OWNER'],
  users:              ALL_ADMIN_ROLES,
  'donation-bonuses': ['ADMIN', 'OWNER'],
  'roulette-stats':   ['ADMIN', 'OWNER'],
  'promo-stats':      ['ADMIN', 'OWNER'],
  payments:           ['OWNER'],
  // Выгрузка аналитики: zip с CSV для аналитика
  analytics:          ['OWNER'],
  polls:              ['ADMIN', 'OWNER'],
  posts:              ['ADMIN', 'OWNER'],
  'launcher-configs': ['OWNER'],
  // Игровые данные: у каждой таблицы свой ключ, id совпадает с путём после /admin/
  'game-data/ornaments': ['ADMIN', 'OWNER'],
  'game-data/synthesis': ['ADMIN', 'OWNER'],
  'game-data/alchemy': ['ADMIN', 'OWNER'],
};

/**
 * Порядок разделов админки — тот же, что в `AdminSidebar`.
 * Используется для входа в админку: `/admin` открывает ПЕРВЫЙ доступный роли раздел
 * (отдельной главной с плитками нет, как и в личном кабинете).
 * При добавлении раздела править ОБА места: этот список и `menuItems` в `AdminSidebar`.
 */
export const ADMIN_PAGE_ORDER: string[] = [
  'users',
  'support',
  'payments',
  'analytics',
  'donation-bonuses',
  'shop',
  'promo-codes',
  'promo-stats',
  'roulette',
  'roulette-stats',
  'polls',
  'posts',
  'referrals',
  'launcher-configs',
  'game-data/ornaments',
  'game-data/synthesis',
  'game-data/alchemy',
];

// Доступ к фичам внутри страниц
export const ADMIN_FEATURE_ACCESS: Record<string, AdminRole[]> = {
  'support:delete-ticket': ['OWNER'],
  'users:change-role': ['OWNER'],
  'users:payments': ['OWNER'],
  'users:events': ['ADMIN', 'OWNER'],
  'users:referrals': ['ADMIN', 'OWNER'],
  'users:raceChange': ['ADMIN', 'OWNER'],
  'users:money': ['ADMIN', 'OWNER'],
  'users:tokens': ['ADMIN', 'OWNER'],
  'users:deleteItems': ALL_ADMIN_ROLES,
  'users:giveItems': ['ADMIN', 'OWNER'],
  'users:bulk-rewards': ['ADMIN', 'OWNER'],
  // Сброс расы у ВСЕХ пользователей и их игровых аккаунтов — массово и необратимо
  'users:reset-races': ['OWNER'],
  // Невыпущенные секции гайда (`/guide`). Игроки — и гости, и залогиненные — видят только то,
  // что перечислено в PUBLIC_GUIDE_SECTIONS; полный список нужен, чтобы вычитать секцию до выкатки.
  // SUPPORT сюда НЕ входит осознанно: у него в кабинете и раньше не было кнопки на гайд.
  'guide:see-unreleased': ['ADMIN', 'OWNER'],
  // Импорт таблицы игровых данных из Google Sheets заменяет её содержимое целиком
  'game-data:import': ['OWNER'],
};

export function canAccessAdminPage(role: string | undefined, pageId: string): boolean {
  const allowedRoles = ADMIN_PAGE_ACCESS[pageId];
  if (!allowedRoles || !role) return false;
  return allowedRoles.includes(role as AdminRole);
}

/** Первый доступный роли раздел админки; `null` — если не доступен ни один. */
export function getFirstAccessibleAdminPage(role: string | undefined): string | null {
  return ADMIN_PAGE_ORDER.find(pageId => canAccessAdminPage(role, pageId)) ?? null;
}

export function canAccessAdminFeature(role: string | undefined, featureId: string): boolean {
  const allowedRoles = ADMIN_FEATURE_ACCESS[featureId];
  if (!allowedRoles || !role) return false;
  return allowedRoles.includes(role as AdminRole);
}

export function isAdminRole(role: string | undefined): boolean {
  if (!role) return false;
  return ALL_ADMIN_ROLES.includes(role as AdminRole);
}
